import React, { Children, Component, isValidElement, ReactElement } from 'react';
import { Formik, FormikValues, FormikActions, FormikProps, yupToFormErrors } from 'formik';
import { Schema } from 'yup';

import WizardContext from './WizardContext';
import { WizardPageProps } from 'buyside/components/presentation/Form/Wizard/WizardParts/WizardPage';

interface WizardProps {
  children?: (ReactElement | null)[];
  initialPage?: number;
  initialValues: FormikValues;
  onSubmit: (
    values: FormikValues,
    bag: FormikActions<FormikValues>,
    next: (values: FormikValues) => void
  ) => void;
  validationSchema?: Schema<any>;
}
interface WizardState {
  page: number;
  values: FormikValues;
}
// Source: https://github.com/jaredpalmer/formik/blob/master/examples/MultistepWizard.js
class Wizard extends Component<WizardProps, WizardState> {
  constructor(props: WizardProps) {
    super(props);
    this.state = {
      page: this.props.initialPage || 0,
      values: props.initialValues
    };
    this.next = this.next.bind(this);
    this.previous = this.previous.bind(this);
    this.validate = this.validate.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  next(values: FormikValues): void {
    this.setState(
      ({ page }): WizardState => ({
        page: Math.min(page + 1, Children.toArray(this.props.children).length - 1),
        values
      })
    );
  }

  previous(): void {
    this.setState(
      (state): Pick<WizardState, 'page'> => ({
        page: Math.max(state.page - 1, 0)
      })
    );
  }

  validate(values: FormikValues): {} | void {
    const activePage = Children.toArray(this.props.children)[this.state.page];

    if (isValidElement<WizardPageProps>(activePage)) {
      if (activePage.props && activePage.props.validationSchema) {
        try {
          activePage.props.validationSchema.validateSync(values, { abortEarly: false });
          return {};
        } catch (errs) {
          return yupToFormErrors(errs);
        }
      } else if (activePage.props && activePage.props.validate) {
        return activePage.props.validate ? activePage.props.validate(values) : {};
      } else {
        return {};
      }
    } else {
      return {};
    }
  }

  handleSubmit(values: FormikValues, bag: FormikActions<FormikValues>): void {
    const { onSubmit } = this.props;
    return onSubmit(values, bag, this.next);
  }

  render(): ReactElement {
    const { children } = this.props;
    const { page, values } = this.state;
    const totalPages = Children.toArray(this.props.children).length - 1;
    const activePage = Children.toArray(children)[page];
    return (
      <Formik
        initialValues={values}
        enableReinitialize={false}
        validate={this.validate}
        onSubmit={this.handleSubmit}
        render={(formikProps: FormikProps<FormikValues>): ReactElement => {
          const handleNext = (): void => {
            const { validateForm, setTouched, setSubmitting } = formikProps;
            validateForm().then((errors): void => {
              if (Object.keys(errors).length) {
                setTouched({});
                setSubmitting(false);
              } else {
                this.next(values);
              }
            });
          };
          return (
            <form onSubmit={formikProps.handleSubmit}>
              <WizardContext.Provider
                value={{
                  page,
                  totalPages,
                  activePage,
                  next: this.next,
                  previous: this.previous,
                  handleNext: handleNext,
                  ...formikProps
                }}
              >
                {activePage}
              </WizardContext.Provider>
            </form>
          );
        }}
      />
    );
  }
}

export default Wizard;
